// Step 5d Phase 4 — envelope encryption for the offline cache.
//
// ARCHITECTURE (v2 §7, Ben 2026-06-13): a device-level random DEK (AES-GCM-256) encrypts
// the offline stores. Each enrolled user's KEK = Argon2id(PIN, per-user salt) wraps a copy
// of the DEK. Unlock = derive KEK → unwrap DEK → decrypt. This reconciles the SHARED
// per-device read cache with the PER-USER PIN: N users each hold a wrapped copy of the
// SAME DEK, so any of them unlocks the one shared cache.
//
// CARDINAL RULE: this only protects local ciphertext at rest. It never mints server trust —
// every server request still carries real tokens. The PIN unlocks bytes, not authority.
//
// Threat model honesty: a 6-digit PIN is low-entropy. Argon2id (memory-hard) is what makes
// brute-force of an EXTRACTED blob expensive; the in-app attempt counter (profiles store)
// only stops casual in-app guessing. The load-bearing mitigations remain Argon2id + the
// 7-day expiry + remote revocation. WebCrypto AES-GCM is native (works offline); only the
// Argon2 WASM needs precaching (hash-wasm inlines it into the bundle → covered by the
// app-shell precache).

import { argon2id } from 'hash-wasm';

// Interactive-unlock Argon2id params — tuned for a tablet (≈64 MB, a few iterations).
// Stored per-profile alongside the salt so a future tuning change can't break old profiles.
export interface KdfParams {
  algorithm: 'argon2id';
  iterations: number;
  memorySizeKiB: number;
  parallelism: number;
}
export const DEFAULT_KDF_PARAMS: KdfParams = { algorithm: 'argon2id', iterations: 3, memorySizeKiB: 65536, parallelism: 1 };

export const DEK_BYTES = 32; // AES-256
const IV_BYTES = 12;         // AES-GCM standard nonce

export interface Envelope { iv: Uint8Array; ct: ArrayBuffer } // stored verbatim in IndexedDB (structured clone)

export const randomBytes = (n: number): Uint8Array => crypto.getRandomValues(new Uint8Array(n));

// KEK derivation: Argon2id(PIN, salt) → 32 raw bytes. Memory-hard by design.
export async function deriveKekRaw(pin: string, salt: Uint8Array, params: KdfParams = DEFAULT_KDF_PARAMS): Promise<Uint8Array> {
  return argon2id({
    password: pin,
    salt,
    iterations: params.iterations,
    memorySize: params.memorySizeKiB,
    parallelism: params.parallelism,
    hashLength: DEK_BYTES,
    outputType: 'binary',
  });
}

const importAesKey = (raw: Uint8Array, usages: KeyUsage[]): Promise<CryptoKey> =>
  crypto.subtle.importKey('raw', raw as BufferSource, 'AES-GCM', false, usages);

// AES-GCM encrypt arbitrary bytes under a raw key → {iv, ct}. Random IV per call (IV
// uniqueness is critical for GCM — never reused). The GCM tag authenticates: a flipped
// ciphertext byte (or wrong key) throws on decrypt rather than returning garbage.
async function aesEncrypt(rawKey: Uint8Array, plaintext: Uint8Array): Promise<Envelope> {
  const key = await importAesKey(rawKey, ['encrypt']);
  const iv = randomBytes(IV_BYTES);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, key, plaintext as BufferSource);
  return { iv, ct };
}
async function aesDecrypt(rawKey: Uint8Array, env: Envelope): Promise<Uint8Array> {
  const key = await importAesKey(rawKey, ['decrypt']);
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: env.iv as BufferSource }, key, env.ct); // throws on auth failure
  return new Uint8Array(pt);
}

// ── DEK lifecycle ──────────────────────────────────────────────────────────────
export const generateDek = (): Uint8Array => randomBytes(DEK_BYTES);

/** Wrap a copy of the device DEK under a user's KEK (enrolment / add-user-to-device). */
export const wrapDek = (dekRaw: Uint8Array, kekRaw: Uint8Array): Promise<Envelope> => aesEncrypt(kekRaw, dekRaw);

/** Unwrap the DEK with a KEK. THROWS on wrong PIN or tampered wrap (GCM auth) — no plaintext leaks. */
export const unwrapDek = (wrapped: Envelope, kekRaw: Uint8Array): Promise<Uint8Array> => aesDecrypt(kekRaw, wrapped);

// ── Value encryption (JSON ⇄ envelope) under the device DEK ─────────────────────
const enc = new TextEncoder();
const dec = new TextDecoder();

export async function encryptJson(dekRaw: Uint8Array, value: unknown): Promise<Envelope> {
  return aesEncrypt(dekRaw, enc.encode(JSON.stringify(value)));
}
export async function decryptJson<T>(dekRaw: Uint8Array, env: Envelope): Promise<T> {
  const bytes = await aesDecrypt(dekRaw, env);
  return JSON.parse(dec.decode(bytes)) as T;
}

/** An IndexedDB record is an encryption envelope iff it carries iv+ct (vs a legacy plaintext row). */
export function isEnvelope(v: unknown): v is Envelope {
  return !!v && typeof v === 'object' && 'iv' in (v as object) && 'ct' in (v as object);
}
